import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FdFieldConfig, FdFieldConfigs } from '../shared/fd-form-components/fd-form-components.module';
import { LoadingService } from '../shared/service/loading.service';
import { HierarchyService } from '../shared/service/hierarchy.service';
import { FdSelectConfig, Item } from '../shared/fd-form-components/fd-select/fd-select.component';
import { finalize } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { ResellerCloverConfigurationService } from './services/reseller-clover-configuration.service';
import { ResellerCloverConfigurationExportModel, ResellerCloverConfigurationModel, ResellerCloverConfigurationSearchModel } from './models/reseller-clover-configuration.model';
import { MatDialog } from '@angular/material/dialog';
import { AddResellersComponent } from './components/add-resellers/add-resellers.component';
import { EditResellersComponent } from './components/edit-resellers/edit-resellers.component';
import { AuthService } from '../shared/service/auth.service';
import { AdminRolesEnum } from '../shared/enums/admin-roles.enum';
import { ReportTypeEnum } from '../shared/enums/report-type.enum';
import { sanitizeSearchFilter } from '../shared/utils/sanitize-search-filter';
import { ExportExcelService } from '../shared/service/export-excel.service';
import { ErrorService } from '../shared/service/error.service';
import { Messages } from '../shared/messages/messages';

@Component({
    selector: 'app-reseller-clover-configuration',
    templateUrl: './reseller-clover-configuration.component.html',
    styleUrls: ['./reseller-clover-configuration.component.scss'],
    standalone: false
})
export class ResellerCloverConfigurationComponent implements OnInit {

  dataSource = new MatTableDataSource<ResellerCloverConfigurationSearchModel>();
  originalDataSource =  new MatTableDataSource<ResellerCloverConfigurationSearchModel>();
  formGroup: UntypedFormGroup;
  fields: FdFieldConfigs;
  pageNumber = 0;
  size = 10;
  totalItens;
  selectedInstitution: string;
  selectedServiceContract: string;
  channelTypeList: Item[];
  channelList: Item[];
  filterAll: Item = { label: 'Todos', value: '' };
  subChannelList: Item[];
  dataExport: ResellerCloverConfigurationSearchModel[] = [];
  xlsx = ReportTypeEnum.XLSX;

  constructor(private formBuilder: UntypedFormBuilder,
    private loadingService: LoadingService,
    private hierarchyService: HierarchyService,
    private dialog: MatDialog,
    private authService: AuthService,
    private resellerCloverConfigurationService: ResellerCloverConfigurationService,
    private exportExcelService: ExportExcelService,
    private errorService: ErrorService,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.startForms();
    this.disableChannelsFields();
  }

  get hasWriteAccess() {
    return this.authService.isUserInRoles([AdminRolesEnum.RESELLER_CLOVER_CONFIGURATION_WRITE]);
  }

  changeSelectedInstitution(institution: string) {
    this.selectedInstitution = institution;
  }

  changeSelectedServiceContract(serviceContract: string) {
    if(!serviceContract || serviceContract===''){
      this.disableChannelsFields();
    }
    this.disableChannelsFields();
    this.selectedServiceContract = serviceContract;
    if(this.formGroup.controls.serviceContract.value) {
      this.formGroup.controls.channelType.enable();
      this.loadChannelTypes(null, this.selectedInstitution);
    }
  }

  loadChannelTypes(filterValue: string, institutionNumber: string): void {
    if (institutionNumber !== '' && this.formGroup.value.serviceContract !== '') {
      this.loadingService.show();
      this.hierarchyService.channelType(institutionNumber)
        .pipe(finalize(() => this.loadingService.hide()))
        .subscribe(data => {
          if (data) {
            this.channelTypeList = data;
            (this.fields.channelType as FdSelectConfig).items = [this.filterAll];
            (this.fields.channelType as FdSelectConfig).items.push(...data);
            this.formGroup.controls.channelType.setValue(this.filterAll.value)
            this.formGroup.controls.channelType.enable();
          }
        }, (error: HttpErrorResponse) => {
          this.formGroup.controls.channelType.disable();
          this.formGroup.controls.channelType.setValue('');
          console.log(error);
        });
    }
    else {
      this.formGroup.controls.channelType.disable();
      this.formGroup.controls.channel.disable();

      this.formGroup.controls.channelType.setValue('');
      this.formGroup.controls.channel.setValue('');
    }
  }

  loadChannels(): void {
    let channelType = this.formGroup.controls.channelType.value;
    if (channelType !== '') {
      this.loadingService.show();
      this.hierarchyService.channel(channelType)
        .pipe(finalize(() => this.loadingService.hide()))
        .subscribe(data => {
          if (data) {
            this.channelList = data;
            (this.fields.channel as FdSelectConfig).items = [this.filterAll];
            (this.fields.channel as FdSelectConfig).items.push(...data);
            this.formGroup.controls.channel.setValue(this.filterAll.value)
            this.formGroup.controls.channel.enable();
          }
        }, (error: HttpErrorResponse) => {
          this.formGroup.controls.channel.disable();
          this.formGroup.controls.channel.setValue('');
          this.formGroup.controls.subChannel.disable();
          this.formGroup.controls.subChannel.setValue('');
          console.log(error);
        });
    }
    else {
      this.formGroup.controls.channel.disable();
      this.formGroup.controls.channel.setValue('');
      this.formGroup.controls.subChannel.disable();
      this.formGroup.controls.subChannel.setValue('');
    }
  }

  loadSubChannels(filterValue?: string): void {
    let channel = this.formGroup.controls.channel.value;
    let channelType = this.formGroup.controls.channelType.value;

    if (!channelType || !channel) {
      return;
    }

    if (filterValue) {
      this.filterDropdowns(filterValue, this.subChannelList, this.fields.subChannel);
      return;
    }

    if (!channel) {
      channel = this.formGroup.value.channel;
    }
    if (!channelType) {
      channelType = this.formGroup.value.channelType;
    }
    if (channel !== '') {
      this.loadingService.show();
      this.hierarchyService.subChannel(channel, channelType)
        .pipe(finalize(() => this.loadingService.hide()))
        .subscribe(data => {
          if (data) {
            this.subChannelList = data;
            (this.fields.subChannel as FdSelectConfig).items = [this.filterAll];
            (this.fields.subChannel as FdSelectConfig).items.push(...data);
            this.formGroup.controls.subChannel.enable();
          }
        }, (error: HttpErrorResponse) => {
          this.formGroup.controls.subChannel.disable();
          this.formGroup.controls.subChannel.setValue('');
        });
    }
    else {
      this.formGroup.controls.subChannel.disable();
      this.formGroup.controls.subChannel.setValue('');
    }
  }

  disableChannelsFields(){
    this.formGroup.controls.channelType.setValue('');
    this.formGroup.controls.channel.setValue('');
    this.formGroup.controls.subChannel.setValue('');
    this.formGroup.controls.channelType.disable();
    this.formGroup.controls.channel.disable();
    this.formGroup.controls.subChannel.disable();
  }

  clearFilters(): void {
    this.formGroup.controls.institution.setValue('');
    this.formGroup.controls.serviceContract.setValue('');
    this.formGroup.controls.serviceContract.disable();
    this.disableChannelsFields();
  }

  editReseller(item: ResellerCloverConfigurationModel) {
    const dialogRef = this.dialog.open(EditResellersComponent, {
      width: '40%',
      height: '85%',
      data: {
        resellerCloverConfigModel: item
      }
    });

    dialogRef.afterClosed().subscribe(_ => {
      this.search();
    })
  }

  addReseller() {
    const dialogRef = this.dialog.open(AddResellersComponent, {
      width: '40%',
      height: '85%',
    });

    dialogRef.afterClosed().subscribe(_ => {
      this.search();
    })
  }

  search() {
    let institution =  this.formControls.institution.value;
    let serviceContract = this.formControls.serviceContract.value;
    let channelType = this.formControls.channelType.value;
    let channel = this.formControls.channel.value;
    let subChannel = this.formControls.subChannel.value;

    if (!serviceContract) {
      return;
    }else{
      this.loadingService.show();
      this.resellerCloverConfigurationService.getResellerCloverConfigurations(institution, serviceContract, channelType, channel, subChannel, this.size, this.pageNumber)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(pageable => {
        this.dataSource.data = pageable.response;
        this.originalDataSource.data = this.cloneArray(this.dataSource.data);
        this.totalItens = pageable.totalItens;
        this.pageNumber = pageable.page;
        this.size = pageable.size;
      });
    }
  }

  changePage(event: any) {
    this.size = event.pageSize;
    this.pageNumber = event.pageIndex;
    this.search();
  }

  cloneArray<T>(arr: T[]): T[] {
    return JSON.parse(JSON.stringify(arr));
  }

  exportResellerClover(reportType: ReportTypeEnum) {
    let institution =  this.formControls.institution.value;
    let serviceContract = this.formControls.serviceContract.value;
    let channelType = this.formControls.channelType.value;
    let channel = this.formControls.channel.value;
    let subChannel = this.formControls.subChannel.value;

    if (serviceContract) {
      this.loadingService.show();
      this.resellerCloverConfigurationService.exportConfiguration(institution, serviceContract, channelType, channel, subChannel)
        .subscribe((data) => {
          this.loadingService.hide();
          this.dataExport = this.normalizeExportData(this.cloneArray(data || []));
          this.renameHeaders();

          this.exportExcelService.exportAsExcelFile(
            this.dataExport, 'reseller-clover' + '_' + institution + '_' + serviceContract + '_'
              + this.datePipe.transform(new Date(), 'dd-MM-yyyy')
          );
        }, (err: HttpErrorResponse) => {
          this.loadingService.hide();
          this.errorService.handleXHRErrorDownload(err, Messages.DOWNLOAD_ERROR);
        });
    }
  }

  normalizeExportData(data: ResellerCloverConfigurationExportModel[]): ResellerCloverConfigurationExportModel[] {
    return data.map(item => {
      const normalizedItem = { ...item } as Record<string, any>;

      Object.keys(normalizedItem).forEach(key => {
        if (normalizedItem[key] === null) {
          normalizedItem[key] = 'Todos';
        }
      });

      return normalizedItem as ResellerCloverConfigurationExportModel;
    });
  }

  renameHeaders() {
    const headerMapping: Array<{ from: keyof ResellerCloverConfigurationExportModel; to: string }> = [
      { from: 'institution', to: 'Instituição' },
      { from: 'serviceContract', to: 'Service Contract' },
      { from: 'channelType', to: 'Tipo de Canal' },
      { from: 'channel', to: 'Canal' },
      { from: 'subChannel', to: 'Sub Canal' },
      { from: 'resellerCode', to: 'Reseller Clover' }
    ];

    this.dataExport = this.dataExport.map(item => {
      const renamedItem: Record<string, any> = {};

      headerMapping.forEach(header => {
        renamedItem[header.to] = item[header.from];
      });

      return renamedItem as ResellerCloverConfigurationExportModel;
    });
  }

  filterDropdowns(filterValue: string, listParam: Item[], field: FdFieldConfig) {
    const list = listParam.filter(x => sanitizeSearchFilter(x.value.toString().trim()).indexOf(filterValue) > -1 || sanitizeSearchFilter(x.label.trim().toLowerCase()).indexOf(filterValue) > -1);
    (field as FdSelectConfig).items = list;
  }


  get formControls() {
    return this.formGroup.controls;
  }

    startForms(): void {

      this.formGroup = this.formBuilder.group({
        institution: ['', Validators.required],
        serviceContract: ['', Validators.required],
        channelType: [''],
        channel: [''],
        subChannel: ['']

      });

      this.fields = {
        institution: {
          label: 'Nº Instituição',
          controlName: 'institution',
          maskCharsReplace: /[. / -]/g,
                    panelWidth: '320px',
          messages: {
            required: 'Informe uma instituição',
            invalid: 'Usuário inválido'
          }
        },
        serviceContract: {
          label: 'Service Contract',
          controlName: 'serviceContract',
                    panelWidth: '320px',
          maskCharsReplace: /[. / -]/g,
          messages: {
            required: 'Informe um service contract',
            invalid: 'Usuário inválido'
          }
        },
        channelType: {
          label: 'Tipo de Canal',
          items: [],
          controlName: 'channelType',
                    panelWidth: '320px',
          messages: {
            required: 'Informe um Tipo de Canal'
          }
        },
        channel: {
          label: 'Canal',
          items: [],
          controlName: 'channel',
                    panelWidth: '320px',
          messages: {
            required: 'Informe um Canal'
          }
        },
        subChannel: {
          label: 'Sub Canal',
          items: [],
          controlName: 'subChannel',
                    panelWidth: '320px',
          messages: {
            required: 'Informe um Sub Canal'
          }
        }
      };
    }

}
